import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';

import type { RenderedNavSection } from '../navigation';
import { CommandPalette } from './CommandPalette';
import { CommandPaletteContext } from './paletteContext';
import { isApplePlatform, isMultilineField } from './shortcut';

/**
 * Monte la palette une fois pour toute la coque, et lui donne ses deux portes :
 * le raccourci `⌘K` (sous Apple) ou `Ctrl+K`, ecoute sur le document, et le champ
 * de la barre superieure (`useCommandPalette().open`).
 *
 * Le raccourci ne **detourne** la touche que la ou la palette s'ouvre : dans une
 * zone de saisie multiligne il est laisse au navigateur et a la zone. Pose sous
 * la coque, donc sous la garde de session : une page de connexion n'en a pas.
 */
export const CommandPaletteProvider = ({
  sections,
  children,
}: {
  readonly sections: readonly RenderedNavSection[];
  readonly children: ReactNode;
}): ReactElement => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      // Sous Apple, `Ctrl+K` est « supprimer jusqu'a la fin de la ligne » dans un
      // champ : seul `⌘K` y ouvre la palette. Ailleurs, `Ctrl+K` ; `⌘K` reste accepte.
      const modifier = event.metaKey || (event.ctrlKey && !isApplePlatform());
      const isShortcut = modifier && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k';

      if (!isShortcut || event.defaultPrevented || isMultilineField(event.target)) {
        return;
      }

      event.preventDefault();
      setOpen(true);
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const controls = useMemo(
    () => ({
      open: () => {
        setOpen(true);
      },
    }),
    [],
  );

  return (
    <CommandPaletteContext.Provider value={controls}>
      {children}
      <CommandPalette
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        sections={sections}
      />
    </CommandPaletteContext.Provider>
  );
};
